import { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import {
  Dialog, DialogContent, Box, Typography, IconButton,
  Button, TextField, MenuItem, CircularProgress,
} from '@mui/material';

const SUBJECTS = [
  'שאלה לגבי המסלולים',
  'בקשת הצעת מחיר לחבילת חומרה',
  'תמיכה טכנית',
  'שאלה כללית',
  'אחר',
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Heebo, sans-serif',
    fontSize: '0.9rem',
    borderRadius: 2,
    '& fieldset': { borderColor: 'rgba(30,58,95,0.2)' },
    '&:hover fieldset': { borderColor: '#1e3a5f' },
    '&.Mui-focused fieldset': { borderColor: '#9a7209' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Heebo, sans-serif',
    fontSize: '0.88rem',
    '&.Mui-focused': { color: '#9a7209' },
  },
};

const sendEmail = (form) =>
  emailjs.send(
    process.env.REACT_APP_EMAILJS_SERVICE_ID,
    process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
    {
      from_name: form.name,
      from_email: form.email,
      synagogue: form.synagogue || '—',
      subject: form.subject,
      message: form.message,
    },
    process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
  );

const ContactDialog = ({ open, onClose, prefill }) => {
  const [form, setForm] = useState({
    name: '', email: '', synagogue: '',
    subject: prefill?.subject || '',
    message: prefill?.message || '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (prefill) {
      setForm(f => ({
        ...f,
        subject: prefill.subject || f.subject,
        message: prefill.message || f.message,
      }));
    }
  }, [prefill]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleClose = () => {
    onClose();
    setTimeout(() => { setSent(false); setForm({ name: '', email: '', synagogue: '', subject: prefill?.subject || '', message: prefill?.message || '' }); }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendEmail(form);
      setSent(true);
    } catch (err) {
      console.error('EmailJS error:', err);
      alert('שגיאה בשליחה, נסו שוב או פנו ישירות ל-ineraigroup@gmail.com');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { direction: 'rtl', borderRadius: 3, maxHeight: '88vh', m: { xs: 1.5, sm: 3 } },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: { xs: 2.5, sm: 3.5 }, pt: 3, pb: 1.5,
        borderBottom: '1px solid rgba(30,58,95,0.08)',
      }}>
        <Typography sx={{ fontFamily: 'Frank Ruhl Libre, serif', fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 900, color: '#1e3a5f' }}>
          צור קשר
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: '#8a94a0', '&:hover': { color: '#1e3a5f', bgcolor: 'rgba(30,58,95,0.06)' } }}>
          <Box component="span" sx={{ fontSize: '1.1rem', lineHeight: 1 }}>✕</Box>
        </IconButton>
      </Box>

      <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3 }}>
        {sent ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography sx={{ fontFamily: 'Frank Ruhl Libre, serif', fontSize: '1.6rem', fontWeight: 900, color: '#1e3a5f', mb: 1.5 }}>
              תודה על פנייתכם
            </Typography>
            <Typography sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.95rem', color: '#5a6978', lineHeight: 1.8, mb: 3 }}>
              ההודעה נשלחה — נחזור אליכם בהקדם.
            </Typography>
            <Button onClick={handleClose} sx={{ color: '#9a7209', fontFamily: 'Heebo, sans-serif', fontWeight: 600, textTransform: 'none' }}>
              סגור
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.2 }}>
            {prefill?.planName && (
              <Box sx={{ bgcolor: 'rgba(154,114,9,0.07)', border: '1px solid rgba(154,114,9,0.2)', borderRadius: 2, px: 2.5, py: 1.5 }}>
                <Typography sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.85rem', color: '#1e3a5f', fontWeight: 600, mb: 0.3 }}>
                  מסלול נבחר: {prefill.planName}
                </Typography>
                <Typography sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.8rem', color: '#7a8898', lineHeight: 1.6 }}>
                  בקרוב תהיה סליקה אונליין לרכישה עצמאית — בינתיים נסדר הכל ישירות מולכם.
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField label="שם מלא" value={form.name} onChange={set('name')} required fullWidth sx={fieldSx} />
              <TextField label="כתובת מייל" type="email" value={form.email} onChange={set('email')} required fullWidth sx={fieldSx} />
            </Box>

            <TextField label="שם בית הכנסת / הארגון (אופציונלי)" value={form.synagogue} onChange={set('synagogue')} fullWidth sx={fieldSx} />

            <TextField select label="נושא הפנייה" value={form.subject} onChange={set('subject')} required fullWidth sx={fieldSx}>
              {SUBJECTS.map(s => (
                <MenuItem key={s} value={s} sx={{ fontFamily: 'Heebo, sans-serif', fontSize: '0.88rem' }}>{s}</MenuItem>
              ))}
            </TextField>

            <TextField label="הודעה" value={form.message} onChange={set('message')} required fullWidth multiline rows={4} sx={fieldSx} />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={sending}
              sx={{
                bgcolor: '#9a7209', color: 'white', fontFamily: 'Heebo, sans-serif', fontWeight: 700,
                fontSize: '0.95rem', py: 1.4, borderRadius: 2,
                boxShadow: '0 6px 20px rgba(154,114,9,0.3)',
                '&:hover': { bgcolor: '#b8860b' },
                '&.Mui-disabled': { bgcolor: 'rgba(154,114,9,0.4)', color: 'white' },
              }}
            >
              {sending ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'שלח הודעה'}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { sendEmail };
export default ContactDialog;
